import type { GameView } from "../../server/protocol";
/** Personal result line for the authenticated seat on a finished match. Built
 * from public fields only (`winner.playerIds`, `result`, `revealedRoles`, all
 * public once the match ends) plus the viewer's own role as a fallback; never
 * rendered for a display or spectator. */
export type PersonalOutcome = { text: string; won: boolean };
type Winner = { team?: string; playerIds?: string[]; reason?: string };
export function personalOutcome(
  game: Pick<GameView, "id" | "finished" | "winner" | "public" | "private">,
  viewerId: string | null,
  publicAudience: boolean,
): PersonalOutcome | null {
  if (!game.finished || !viewerId || publicAudience) return null;
  const p = game.public,
    w = (game.winner ?? {}) as Winner,
    ids = Array.isArray(w.playerIds) ? w.playerIds : [];
  if (!(p.players ?? []).some((v: { id: string }) => v.id === viewerId))
    return null;
  const won = ids.includes(viewerId);
  const role: string | undefined =
    p.revealedRoles?.find((r: { playerId: string }) => r.playerId === viewerId)
      ?.role ?? game.private?.role;
  if (game.id === "impostor" || game.id === "alibi") {
    const r = p.result ?? {},
      noun = game.id === "alibi" ? "spy" : "impostor",
      them = r.impostorId ?? r.spyId,
      outcome = String(r.outcome ?? "");
    if (!them) return null;
    if (them === viewerId)
      return {
        won,
        text: won
          ? outcome === "guessed-word" || outcome === "correct-guess"
            ? `You were the ${noun} — caught, but you guessed ${game.id === "alibi" ? "the location" : "the word"}!`
            : outcome === "escaped-tie"
              ? `You were the ${noun} — the tie let you slip away.`
              : `You were the ${noun} — they accused the wrong person.`
          : `You were the ${noun} — caught.`,
      };
    return {
      won,
      text: won
        ? `You won — Locals caught the ${noun}.`
        : outcome === "guessed-word" || outcome === "correct-guess"
          ? `You lost — the ${noun} guessed ${game.id === "alibi" ? "the location" : "the word"}.`
          : `You lost — the ${noun} got away.`,
    };
  }
  if (game.id === "mafia") {
    const team = w.team === "mafia" ? "the Mafia" : "the town";
    if (role === "mafia")
      return {
        won,
        text: won ? "You won with the Mafia." : "You lost — the town found the Mafia.",
      };
    return {
      won,
      text: won
        ? "You won — the town found the Mafia."
        : `You lost — ${team} took the town.`,
    };
  }
  if (game.id === "secret-hitler") {
    const winners = w.team === "fascist" ? "Fascists" : "Liberals";
    if (role === "hitler")
      return {
        won,
        text: won ? "You were Hitler — and the Fascists won." : "You were Hitler — the Liberals won.",
      };
    return {
      won,
      text: won ? `You won — ${winners} take it.` : `You lost — ${winners} won.`,
    };
  }
  if (game.id === "liar-dice")
    return won
      ? { won, text: "You won — last cup standing." }
      : { won, text: "You’re out this time." };
  return null;
}
/** Wavelength-style verdict for a round's points, from the public scoring bands. */
export function spectrumRoundVerdict(
  points: number,
  bands: ReadonlyArray<{ points: number }> | undefined,
): string {
  const max = Math.max(1, ...(bands ?? [{ points: 4 }]).map((b) => b.points));
  const ratio = points / max;
  return ratio >= 1
    ? "Psychic!"
    : ratio >= 0.75
      ? "So close"
      : ratio >= 0.5
        ? "Warm"
        : ratio > 0
          ? "Lukewarm"
          : "Way off";
}
/** Verdict for the whole team on the final score. */
export function spectrumFinalVerdict(total: number, maximum: number): string {
  const ratio = maximum > 0 ? total / maximum : 0;
  return ratio >= 0.85
    ? "Mind melders."
    : ratio >= 0.6
      ? "On the same wavelength."
      : ratio >= 0.35
        ? "Getting warmer."
        : "Still tuning in.";
}
